// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, Menu, MenuItem } from '@mui/material';
import { type FC } from 'react';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { useLanguageSelector } from './use-language-selector';

/**
 * Component that allows the user to switch between different languages.
 *
 * @remarks
 * This component uses the `useLanguageSelector` hook to manage the state of the
 * language selector.
 */
export const LanguageSelector: FC = () => {
  const {
    anchorEl,
    open,
    handleMenu,
    handleClose,
    localesIds,
    currentLocale,
    onChangeLanguage,
    language,
  } = useLanguageSelector();

  return (
    <>
      <Button
        onClick={handleMenu}
        variant="text"
        color="inherit"
        data-testid="locale-button"
      >
        <AppIcon
          data-testid={`locale-icon-${currentLocale.icon}`}
          icon={currentLocale.icon}
          size="lg"
          className="!h-4 !w-4"
        />
        <span className="!text-sm !font-medium !text-gray-100">
          {currentLocale.value}
        </span>
        <AppIcon
          icon="ArrowDown"
          data-testid="dropdown-arrow"
          className={`!h-4 !w-4 !text-gray-400 !transition-transform !duration-200 ${
            open ? '!rotate-180' : '!rotate-0'
          }`}
        />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            className:
              '!min-w-[160px] !mt-1 !rounded-md !overflow-hidden !border !border-gray-200 !bg-white !shadow-md',
          },
        }}
      >
        {localesIds.map(locale => (
          <MenuItem
            key={locale.id}
            onClick={() => {
              onChangeLanguage(locale.id);
              handleClose();
            }}
            selected={locale.id === language}
            data-testid={`locale-${locale.id}`}
            className={`!flex !items-center !gap-3 !px-3 !py-2 !font-poppins ${
              locale.id === language
                ? '!bg-primary/10 !text-primary hover:!bg-primary/15'
                : '!text-gray-700 hover:!bg-gray-100'
            }`}
          >
            <AppIcon
              data-testid={`icon-${locale.icon}`}
              icon={locale.icon}
              size="lg"
              className="!h-5 !w-5"
            />
            <span className="!text-sm">{locale.label}</span>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
